import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Radio } from "lucide-react";
import { api } from "@/lib/api";

const ACTION_STYLE = {
  Blocked: "text-red-400 bg-red-500/10 border-red-500/20",
  Flagged: "text-amber-400 bg-amber-500/10 border-amber-500/20",
};

const timeAgo = (iso) => {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};

export const ThreatFeed = () => {
  const [events, setEvents] = useState(null);
  const [source, setSource] = useState(null);
  const sinceRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const params = sinceRef.current ? { since: sinceRef.current } : {};
        const { data } = await api.get("/dashboard/threat-feed", { params });
        if (!alive) return;
        setSource(data.data_source);
        if (data.events.length) {
          sinceRef.current = data.events[0].timestamp;
          setEvents((prev) => {
            const seen = new Set((prev || []).map((e) => e.id));
            const fresh = data.events.filter((e) => !seen.has(e.id));
            return [...fresh, ...(prev || [])].slice(0, 7);
          });
        } else if (events === null) {
          setEvents((prev) => prev || []);
        }
      } catch {
        if (alive) setEvents((prev) => prev || []);
      }
    };
    poll();
    const t = setInterval(poll, 4000);
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="threat-feed" className="rounded-xl bg-[#0B101A] border border-white/8 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-red-400" />
          <h3 className="font-display text-lg text-white">Live threat feed</h3>
        </div>
        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-wider uppercase text-slate-500">
          <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse-glow" />
          {source === "live" ? "Live · your traffic" : source === "demo" ? "Demo stream" : "Connecting…"}
        </span>
      </div>
      {!events || events.length === 0 ? (
        <p data-testid="threat-feed-empty" className="font-mono text-xs text-slate-500 py-3">
          Watching for threats… blocked and flagged clicks will stream in here.
        </p>
      ) : (
        <div className="space-y-1.5">
          <AnimatePresence initial={false}>
            {events.map((e) => (
              <motion.div
                key={e.id}
                data-testid="threat-feed-event"
                initial={{ opacity: 0, x: -24, height: 0 }}
                animate={{ opacity: 1, x: 0, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.35 }}
                className="overflow-hidden"
              >
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-[#040914] border border-white/5 px-4 py-2.5">
                  <span className={`inline-flex px-2 py-0.5 rounded border font-mono text-[9px] tracking-wider uppercase ${ACTION_STYLE[e.action] || ""}`}>
                    {e.action}
                  </span>
                  <span className="font-mono text-xs text-white">{e.ip}</span>
                  <span className="text-xs text-slate-400">{e.country}</span>
                  <span className="text-xs text-slate-500 hidden sm:inline">{e.device}</span>
                  <span className="font-mono text-xs text-red-400">{e.score}/100</span>
                  {e.signals?.includes("custom_rule:country") || e.signals?.some((s) => s.startsWith("custom_rule")) ? (
                    <span className="font-mono text-[9px] tracking-wider uppercase text-cyan-neon">custom rule</span>
                  ) : null}
                  <span className="ml-auto font-mono text-[10px] text-slate-600">{timeAgo(e.timestamp)}</span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};
